import React, { createRef, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { FlexBox, Typography, Button, Box, Card, color } from '@kis-technologies/kis-ui';
import { selectors as mediaSelectors } from 'store/media';
import { selectors as configurationsSelectors } from 'store/configurations';
import { isValidVideo } from 'utils/validation';
import { VideoMediaDetails } from './VideoMediaDetails';
import { MediaText } from '../MediaText';
import { VideoMediaListProps, MediaResponse } from '../../../types';

export const VideoMediaList: React.FC<VideoMediaListProps> = (props: VideoMediaListProps) => {
  const { isActive, mediaType } = props;
  const { successful, response, pending, error } = useSelector(mediaSelectors.selectAction);
  const loading = useSelector(configurationsSelectors.selectPending);
  const [uploadVideoError, setUploadVideoError] = useState(error);
  const fileUploadRef = createRef<HTMLInputElement>();

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = get(e, ['target', 'files', 0]) as File | undefined;
    if (file) {
      const err = isValidVideo(file);
      if (err) {
        setUploadVideoError(err);
      } else {
        setUploadVideoError('');
        props.uploadVideo({ file, name: file.name, type: mediaType });
      }
    }
  };

  const triggerFileSelector = () => {
    if (fileUploadRef.current) fileUploadRef.current.click();
  };

  const onMakeActive = () => {
    props.updateConfig({ type: mediaType });
  };

  if (successful && response) {
    props.onSuccess(response);
  }

  return (
    <>
      <input
        type="file"
        id="file"
        ref={fileUploadRef}
        style={{ display: 'none' }}
        onChange={handleFileSelection}
        accept=".mp4, .mov"
        data-testid="file"
      />
      {props.activeImage && props.activeImage.name ? (
        <FlexBox fullWidth>
          <Box width={650}>
            <Card raised hasBorderRadius>
              <Card.Title
                gutter={5}
                title={
                  <FlexBox direction="row" fullWidth justify="between">
                    <Typography fontSize={18} fontWeight={600}>
                      Video
                    </Typography>
                    {isActive && !loading ? (
                      <Typography fontSize={18} fontWeight={600} color={color.PRIMARY}>
                        ACTIVE
                      </Typography>
                    ) : (
                      <Button customSize="x-small" appearance="primary" onClick={onMakeActive} isLoading={loading}>
                        Make Active
                      </Button>
                    )}
                  </FlexBox>
                }
              />
              <Card.Content>
                <VideoMediaDetails
                  video={props.activeImage.name}
                  error={error || uploadVideoError}
                  onAddVideoClick={triggerFileSelector}
                  onVideoDelete={() => props.onDelete(props.activeImage as MediaResponse)}
                  isLoading={pending}
                />
              </Card.Content>
            </Card>
          </Box>
        </FlexBox>
      ) : (
        <Box width={650}>
          <Card raised hasBorderRadius>
            <Card.Title
              gutter={5}
              title={
                <Typography fontSize={18} fontWeight={600}>
                  Video
                </Typography>
              }
            />
            <Card.Content>
              <Box>
                <MediaText mediaType={mediaType} />
                {(error || uploadVideoError) && <Typography color={color.RED}>{error || uploadVideoError}</Typography>}
                <Button
                  customSize="small"
                  width={160}
                  onClick={triggerFileSelector}
                  isLoading={pending}
                  data-testid="add-new-video-button"
                >
                  Upload Video
                </Button>
              </Box>
            </Card.Content>
          </Card>
        </Box>
      )}
    </>
  );
};
